import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { useApp } from "@/context/AppContext";

/** /socio/registro — fully localized ES / FR / EN. "Socio referidor" is kept
 *  untranslated as a proper noun across all three languages. */
const T = {
  es: {
    sentTitle: "Solicitud recibida",
    sentLead: "Le contactaremos en 48h con su código de referido.",
    sentBack: "Volver al inicio",
    title: "Conviértase en Socio referidor",
    lead: "$9 USD al mes por cada médico referido que paga su suscripción (después de su mes gratis), transferidos a su cuenta bancaria en Ecuador.",
    nameLbl: "Nombre completo *",
    emailLbl: "Email *",
    phoneLbl: "Teléfono (opcional)",
    terms: <>Acepto los <Link to="/legal/terminos#referidos" className="text-blue-600 hover:underline">términos del programa de referidos</Link></>, // kept — unrelated to doctor trial copy
    submit: "Enviar solicitud",
    missing: "Completa los campos requeridos",
    mustAcceptTerms: "Debes aceptar los términos",
    haveAccount: "¿Ya tienes cuenta?",
    signIn: "Ingresar",
    errorGeneric: "Error",
  },
  fr: {
    sentTitle: "Demande reçue",
    sentLead: "Nous vous contacterons sous 48h avec votre code de parrainage.",
    sentBack: "Retour à l'accueil",
    title: "Devenir Socio referidor",
    lead: "$9 USD par mois pour chaque médecin parrainé qui paie son abonnement (après son mois gratuit), versés par virement sur votre compte bancaire en Équateur.",
    nameLbl: "Nom complet *",
    emailLbl: "Email *",
    phoneLbl: "Téléphone (facultatif)",
    terms: <>J'accepte les <Link to="/legal/terminos#referidos" className="text-blue-600 hover:underline">conditions du programme de parrainage</Link></>,
    submit: "Envoyer la demande",
    missing: "Complétez les champs obligatoires",
    mustAcceptTerms: "Vous devez accepter les conditions",
    haveAccount: "Vous avez déjà un compte ?",
    signIn: "Se connecter",
    errorGeneric: "Erreur",
  },
  en: {
    sentTitle: "Request received",
    sentLead: "We'll get back to you within 48h with your referral code.",
    sentBack: "Back to home",
    title: "Become a Socio referidor",
    lead: "$9 USD per month for each referred doctor who pays their subscription (after their free month), transferred to your bank account in Ecuador.",
    nameLbl: "Full name *",
    emailLbl: "Email *",
    phoneLbl: "Phone (optional)",
    terms: <>I accept the <Link to="/legal/terminos#referidos" className="text-blue-600 hover:underline">terms of the referral program</Link></>,
    submit: "Submit request",
    missing: "Fill in the required fields",
    mustAcceptTerms: "You must accept the terms",
    haveAccount: "Already have an account?",
    signIn: "Sign in",
    errorGeneric: "Error",
  },
};

export default function PartnerRegister() {
  const nav = useNavigate();
  const { lang } = useApp();
  const t = T[lang] || T.es;
  const [form, setForm] = useState({ name: "", email: "", phone: "", accepted_terms: false });
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email) return toast.error(t.missing);
    if (!form.accepted_terms) return toast.error(t.mustAcceptTerms);
    try {
      await api.post("/partner/register", form);
      setSent(true);
    } catch (e) { toast.error(e.response?.data?.detail || t.errorGeneric); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-10">
      <Card><CardContent className="p-6 sm:p-8">
        {sent ? (
          <div className="text-center py-4">
            <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-3"/>
            <h1 className="text-xl font-bold text-slate-900">{t.sentTitle}</h1>
            <p className="text-sm text-slate-600 mt-2">{t.sentLead}</p>
            <Button onClick={() => nav("/")} className="mt-5 bg-blue-600 hover:bg-blue-700">{t.sentBack}</Button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900">{t.title}</h1>
            <p className="text-sm text-slate-500 mt-1">{t.lead}</p>
            <div className="space-y-3 mt-5">
              <div><Label>{t.nameLbl}</Label>
                <Input data-testid="partner-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/></div>
              <div><Label>{t.emailLbl}</Label>
                <Input data-testid="partner-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}/></div>
              <div><Label>{t.phoneLbl}</Label>
                <Input data-testid="partner-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}/></div>
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="partner-terms" checked={form.accepted_terms} onChange={e => setForm({...form, accepted_terms: e.target.checked})} className="mt-1"/>
                <span>{t.terms}</span>
              </label>
              <Button data-testid="partner-submit" onClick={submit} className="w-full mt-4 bg-blue-600 hover:bg-blue-700">{t.submit}</Button>
              <div className="text-center text-xs text-slate-500 mt-3">
                {t.haveAccount} <Link to="/socio/login" className="text-blue-600 hover:underline">{t.signIn}</Link>
              </div>
            </div>
          </>
        )}
      </CardContent></Card>
    </div>
  );
}
